import { doc, getDoc, setDoc, serverTimestamp, writeBatch } from "firebase/firestore";
import { db } from "../firebase";

export function userSettingsRef(userId) {
  if (!userId) return null;
  return doc(db, "users", userId);
}

export function userPublicRef(userId) {
  if (!userId) return null;
  return doc(db, "usersPublic", userId);
}

export async function fetchUserSettings(userId) {
  const ref = userSettingsRef(userId);
  if (!ref) return null;
  const snap = await getDoc(ref);
  if (!snap.exists()) return null;
  return snap.data() || null;
}

export async function updateUserSettings(userId, updates) {
  if (!userId) return;
  const ref = userSettingsRef(userId);
  if (!ref) return;
  await setDoc(
    ref,
    {
      ...updates,
      updatedAt: serverTimestamp(),
    },
    { merge: true }
  );
}

export async function addArchivedPoll(userId, pollId, archivedPolls = []) {
  if (!userId || !pollId) return;
  if (archivedPolls.includes(pollId)) return;
  return updateUserSettings(userId, { archivedPolls: [...archivedPolls, pollId] });
}

export async function removeArchivedPoll(userId, pollId, archivedPolls = []) {
  if (!userId || !pollId) return;
  if (!archivedPolls.includes(pollId)) return;
  return updateUserSettings(userId, {
    archivedPolls: archivedPolls.filter((id) => id !== pollId),
  });
}

export async function setCalendarSyncPreference(userId, preference) {
  if (!userId) return;
  return updateUserSettings(userId, { calendarSyncPreference: preference });
}

export async function setGroupColor(userId, groupColors, groupId, color) {
  if (!userId || !groupId) return;
  return updateUserSettings(userId, {
    groupColors: {
      ...(groupColors || {}),
      [groupId]: color,
    },
  });
}

/**
 * Updates per-user mark-authoring preferences. Client-only — no public
 * mirror because the auto-vote engine doesn't read these.
 */
export async function setMarkPreferences(userId, patch) {
  if (!userId) return;
  const userRef = userSettingsRef(userId);
  if (!userRef) return;
  const settingsPatch = {};
  if (patch.defaultMarkDurationHours !== undefined) {
    settingsPatch.defaultMarkDurationHours = Number(patch.defaultMarkDurationHours);
  }
  if (patch.defaultMarkStartHour !== undefined) {
    settingsPatch.defaultMarkStartHour = patch.defaultMarkStartHour ?? null;
  }
  await setDoc(
    userRef,
    { settings: settingsPatch, updatedAt: serverTimestamp() },
    { merge: true }
  );
}

/**
 * Opt-in calendar-email consent (Model Y). Authoritative copy lives at
 * users/{uid}.settings.shareEmailForCalendar (private); mirrored to the
 * top-level usersPublic/{uid}.shareEmailForCalendar so the server finalize
 * gate can read another attendee's consent. Default false everywhere; value
 * is coerced to a strict boolean.
 */
export async function setShareEmailForCalendar(userId, value) {
  if (!userId) return;
  const userRef = userSettingsRef(userId);
  const publicRef = userPublicRef(userId);
  if (!userRef || !publicRef) return;
  // Fail-closed: only an explicit boolean true grants consent. Any truthy
  // non-boolean (e.g. "false", "yes", {}, 1) coerces to false — matching the
  // read side (useUserSettings, which is `=== true`).
  const flag = value === true;

  const batch = writeBatch(db);
  batch.set(
    userRef,
    { settings: { shareEmailForCalendar: flag }, updatedAt: serverTimestamp() },
    { merge: true }
  );
  batch.set(
    publicRef,
    { shareEmailForCalendar: flag, updatedAt: serverTimestamp() },
    { merge: true }
  );
  await batch.commit();
}

export async function saveUserSettings(userId, userData, publicData) {
  const userRef = userSettingsRef(userId);
  const publicRef = userPublicRef(userId);
  if (!userRef || !publicRef) return;

  await setDoc(
    userRef,
    {
      ...userData,
      updatedAt: serverTimestamp(),
    },
    { merge: true }
  );

  await setDoc(
    publicRef,
    {
      ...publicData,
      updatedAt: serverTimestamp(),
    },
    { merge: true }
  );
}
